import { createContext, FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getMonthlyCreditsByType, getTotalCreditsByDashboardType } from "../../../api/broker";
import {
  BarFilters,
  CreditosMensuales,
  CreditosOrganizacion,
  initalBarFilters,
  MonthYear,
} from "../../../interfaces/SuperAdmin/Pages/iDataCreditos";
import { DashboardType } from "../../../interfaces/SuperAdmin/Pages/iDataDashboard";

interface SingleOrgDashboardContextProps {
  totalCredits: CreditosOrganizacion[];
  monthlyCredits: CreditosMensuales[];
  startMonth: MonthYear | undefined;
  months: number;
  monthlyCreditsFilters: BarFilters;
  isLoadingTotalCredits: boolean;
  isLoadingMonthlyCredits: boolean;
  updateStartingMonth: (month: number, year: number) => void;
  changeMonths: (value: number) => void;
  handleToggleMonthlyCredit: (value: string) => void;
}

const SingleOrgDashboardContext = createContext<SingleOrgDashboardContextProps>(
  {} as SingleOrgDashboardContextProps
);

interface Props {
  children: React.ReactNode;
}

const SingleOrgDashboardProvider: FC<Props> = ({ children }) => {
  // credits tendrá los créditos, y el tipo dependerá de qué filtro tenga
  const [totalCredits, setTotalCredits] = useState<CreditosOrganizacion[]>([]);
  const [monthlyCredits, setMonthlyCredits] = useState<CreditosMensuales[]>([]);

  const [months, setMonths] = useState(12);
  const [startMonth, setStartMonth] = useState<MonthYear | undefined>(
    undefined
  );
  const [monthlyCreditsFilters, setMonthlyCreditsFilters] =
    useState<BarFilters>(initalBarFilters);

  const [isLoadingTotalCredits, setIsLoadingTotalCredits] = useState(true);
  const [isLoadingMonthlyCredits, setIsLoadingMonthlyCredits] = useState(true);

  const { idOrganization } = useParams();

  // Fetch de creditos totales de una organización
  const fetchTotalCreditos = async () => {
    setIsLoadingTotalCredits(true);
    try {
      const totalRes = await getTotalCreditsByDashboardType(
        DashboardType.SINGLE_ORG,
        {
          id_organizacion: idOrganization,
        }
      );

      if (totalRes.data.statusCode === 200) {
        setTotalCredits(totalRes.data.body as CreditosOrganizacion[]);
      } else {
        toast.error("Error al obtener los créditos totales");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los créditos totales");
    } finally {
      setIsLoadingTotalCredits(false);
    }
  };

  // Fetch de creditos mensuales de una organización
  const fetchMonthlyCredits = async (
    idOrganization: string,
    startMonth: MonthYear | undefined,
    months: string
  ) => {
    setIsLoadingMonthlyCredits(true);
    try {
      // Con esta linea formateamos startMonth para que quede en el formato 'MM/YYYY'
      let firstMonth = startMonth
        ? `${startMonth.month}/${startMonth.year}`
        : undefined;

      const monthlyRes = await getMonthlyCreditsByType(
        DashboardType.SINGLE_ORG,
        idOrganization,
        firstMonth,
        months
      );

      if (monthlyRes.data.statusCode === 200) {
        if (!startMonth) {
          const { start_month, start_year } = monthlyRes.data.body;
          setStartMonth({ year: start_year, month: start_month });
        }
        setMonthlyCredits(monthlyRes.data.body.sublimaciones);
      } else {
        toast.error("Error al obtener los créditos mensuales");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los créditos mensuales");
    } finally {
      setIsLoadingMonthlyCredits(false);
    }
  };

  // Ejecutamos el fetch al montar el componente
  useEffect(() => {
    fetchTotalCreditos();
  }, []);

  // Ejecutamos el fetch cada que cambie un parámetro
  useEffect(() => {
    if (idOrganization) {
      fetchMonthlyCredits(idOrganization, startMonth, String(months));
    }
  }, [idOrganization, months, startMonth]);

  // Funcion para cambiar la cantidad de meses
  const changeMonths = (value: number) => {
    setMonths(value);
  };

  // Funcion para actualizar el mes de inicio
  const updateStartingMonth = (month: number, year: number) => {
    setStartMonth({ month, year });
  };

  // Función para cambiar filtros
  const handleToggleMonthlyCredit = (value: string) => {
    setMonthlyCreditsFilters((prev) => ({
      ...prev,
      credits: prev.credits.map((credit) =>
        credit.value === value
          ? { ...credit, isActive: !credit.isActive }
          : credit
      ),
    }));
  };

  return (
    <SingleOrgDashboardContext.Provider
      value={{
        totalCredits,
        monthlyCredits,
        startMonth,
        months,
        monthlyCreditsFilters,
        isLoadingTotalCredits,
        isLoadingMonthlyCredits,
        updateStartingMonth,
        changeMonths,
        handleToggleMonthlyCredit,
      }}
    >
      {children}
    </SingleOrgDashboardContext.Provider>
  );
};

const useSingleOrgDashboard = () => useContext(SingleOrgDashboardContext);

export { SingleOrgDashboardProvider, useSingleOrgDashboard };

